import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as ArrowDown } from 'src/Images/icons/common/arrowdown.svg';
import SelectPurchaseOrderEmptyState from 'src/Images/icons/vehicle-tracking/select-po-empty-state.svg';
import SelectedPurchaseOrderEmptyState from 'src/Images/icons/vehicle-tracking/selected-po-empty-state.svg';
import {
  getBusinessPartnersWithOpenPoDropdownOptions,
  getVehicleTrackingWorkflows,
  getPurchaseOrdersAndLineItemData,
  getPlants,
  getTenantPermissions,
} from 'src/redux/Actions/VehicleTracking';
import 'src/style/createVehicleTracking.css';
import { Drawer } from '@mui/material';
import VehicleDetail from 'src/components/VehicleTrackingCreate/Inward/VehicleDetails';
import { ReactComponent as DetailLinkIcon } from 'src/Images/icons/common/detailLinkIcon.svg';
import { useNavigate } from 'react-router-dom';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';

function BasicDetails({
  formData,
  selectedPurchaseOrder,
  setSelectedPurchaseOrder,
  handleOnChange,
  formErrors,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // reducers
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const authReducer = useSelector((state) => state.AuthReducer);

  const {
    hasPermission,
    positionPermissions,
    teamPermissions,
    isPermissionsLoaded,
  } = usePermissions();

  const noPermissionsAssigned =
    isPermissionsLoaded &&
    (positionPermissions?.length === 0 || !positionPermissions) &&
    (teamPermissions?.length === 0 || !teamPermissions);

  const hasInwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.INWARD.CREATE,
  );
  const hasOutwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE,
  );

  // states
  const [selectedBusinessPartner, setSelectedBusinessPartner] = useState(null);
  const [businessPartnerSearchTerm, setBusinessPartnerSearchTerm] =
    useState('');
  const businessPartnersWithOpenPo =
    vehicleTrackingDataReducer?.businessPartnersWithOpenPo || [];
  const tenantPermission = vehicleTrackingDataReducer?.tenantPermission || [];

  const [purchaseOrderSearchedTerm, setPurchaseOrderSearchedTerm] =
    useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [openPoTableIndexes, setOpenPoTableIndexes] = useState([]);
  const [isVahanVerificationDrawerOpen, setIsVahanVerificationDrawerOpen] =
    useState(false);
  const tenantId = authReducer?.userData?.tenant?.id;
  const [showVahanDrawerOpenButton, setShowVahanDrawerOpenButton] =
    useState(false);

  // variables
  const themeColor = authReducer?.userData?.tenant?.theme;
  const organisationId = authReducer.userData.organisations?.id;
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const { plantsData } = vehicleTrackingDataReducer;
  const { totalPages } =
    vehicleTrackingDataReducer.purchaseOrderAndLineItemData;
  const availableWorkflows = vehicleTrackingDataReducer.workflows;
  const categoryKeyMapping = {
    vendor: 2,
    customer: 1,
  };
  const filteredPurchaseOrderList =
    vehicleTrackingDataReducer?.purchaseOrderAndLineItemData
      ?.purchaseOrderList || [];

  // hooks
  useEffect(() => {
    if (organisationId) {
      dispatch(getVehicleTrackingWorkflows(organisationId, '1')); // hardcoded to inward
      dispatch(getPlants(tenantOrganisationId));
    }
  }, [dispatch, organisationId]);

  useEffect(() => {
    if (tenantId) {
      dispatch(getTenantPermissions(tenantId));
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (organisationId && selectedBusinessPartner) {
      dispatch(
        getPurchaseOrdersAndLineItemData({
          poID: purchaseOrderSearchedTerm,
          business_partner_unique_number: selectedBusinessPartner,
          tenant_organisation_id: organisationId,
          page_number: pageNumber,
        }),
      );
    }
  }, [purchaseOrderSearchedTerm, selectedBusinessPartner, pageNumber]);

  useEffect(() => {
    if (organisationId) {
      dispatch(
        getBusinessPartnersWithOpenPoDropdownOptions(
          organisationId,
          businessPartnerSearchTerm,
          categoryKeyMapping.vendor,
        ),
      );
    }
  }, [organisationId, businessPartnerSearchTerm]);

  useEffect(() => {
    if (filteredPurchaseOrderList?.length) {
      setOpenPoTableIndexes(filteredPurchaseOrderList.map((_, index) => index));
    }
  }, [filteredPurchaseOrderList]);

  // function
  const plantDataOption =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowDataOption =
    availableWorkflows?.map((availableWorkflow) => ({
      value: availableWorkflow.id,
      label: availableWorkflow.name,
    })) || [];

  const businessPartnersWithOpenPoOptions =
    businessPartnersWithOpenPo?.map((bussinessPartnerObj) => ({
      value: bussinessPartnerObj.business_partner_unique_number,
      label: (
        <div className="div-flex-row div-align-center cg-10">
          {bussinessPartnerObj.partner_category?.id && (
            <>
              <div
                className={`text-bold ${
                  { 1: 'blue-pill', 2: 'yellow-pill' }[
                    bussinessPartnerObj.partner_category?.id
                  ] || ''
                }`}
              >
                {{
                  1: 'C',
                  2: 'V',
                }[bussinessPartnerObj.partner_category?.id] || ''}
              </div>
              <div className="divider-bar"></div>
            </>
          )}

          <div className="business-partner-option-text">
            {bussinessPartnerObj.business_partner_sap_code && (
              <>{bussinessPartnerObj.business_partner_sap_code} - </>
            )}
            {bussinessPartnerObj.name}
          </div>
        </div>
      ),
    })) || [];

  const handleBusinessPartnerSelectChange = (event) => {
    setSelectedBusinessPartner(event.target.value);
  };
  const [isSearching, setIsSearching] = useState(false);
  const searchLockRef = useRef(false);

  const handlePurchaseOrderSearchSubmit = () => {
    if (searchLockRef.current) return;
    if (!organisationId || !purchaseOrderSearchedTerm) return;
    searchLockRef.current = true;
    setIsSearching(true);
    dispatch(
      getPurchaseOrdersAndLineItemData({
        poID: purchaseOrderSearchedTerm,
        business_partner_unique_number: selectedBusinessPartner || '',
        tenant_organisation_id: organisationId,
        page_number: pageNumber,
      }),
    ).finally(() => {
      searchLockRef.current = false;
      setIsSearching(false);
    });
  };

  const toggleAccordion = (index) => {
    setOpenPoTableIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  function handleCollapseTableAccordion(isChecked) {
    if (isChecked) {
      setOpenPoTableIndexes([]);
    } else {
      setOpenPoTableIndexes(filteredPurchaseOrderList.map((_, index) => index));
    }
  }

  const handlePurchaseOrderSelection = (e, purchaseOrderObj) => {
    setSelectedPurchaseOrder((prevSelected) => {
      if (e.target.checked) {
        if (!prevSelected.some((poObj) => poObj.id === purchaseOrderObj.id)) {
          return [...prevSelected, purchaseOrderObj];
        }
        return prevSelected;
      }
      return prevSelected.filter((poObj) => poObj.id !== purchaseOrderObj.id);
    });
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;
  const isVahanIntegrationEnabled = tenantPermission?.isVahanIntegrationEnabled;

  const handleBlur = () => {
    setTimeout(() => setShowVahanDrawerOpenButton(true), 120);
  };

  const displayVahanDrawerButton =
    isVahanIntegrationEnabled && showVahanDrawerOpenButton;

  const handleMoveToCreateOutward = () => {
    navigate('/outward/create/');
  };

  return (
    <div className="create-form-container">
      <Row className="g-0 create-form-row">
        <Col
          className={`vehicle-tracking-theme-scrollable-container-${themeColor} scrollable-container `}
          xxl={8}
          xl={8}
          lg={8}
        >
          <div className="col-container">
            {/* vehicle tracking basic detail card */}
            <div className="container-card-shadow border-radius-4 container-padding-5">
              <div className="container-padding-10">
                <Row>
                  <Col xxl={3} xl={3} lg={4} md={4}>
                    <div className="div-flex-column">
                      <div className="div-flex-row">
                        <span className="text-sm text-bold">
                          Vehicle Number
                          <span className="text-red">&nbsp;*</span>
                        </span>
                      </div>
                      <div className="vehicle-number-input-wrapper">
                        <input
                          name="vehicleNumber"
                          data-required-field
                          data-testid="vehicle-number-input"
                          className={`vehicle-number-input margin-top-5 ${
                            formErrors.vehicleNumber
                              ? 'form-input-error'
                              : 'form-input-section'
                          }`}
                          type="text"
                          value={formData.vehicleNumber || ''}
                          onChange={(e) => {
                            const uppercaseValue = e.target.value.toUpperCase();
                            handleOnChange({
                              target: {
                                name: e.target.name,
                                value: uppercaseValue,
                              },
                            });
                          }}
                          onFocus={() => setShowVahanDrawerOpenButton(false)}
                          onBlur={handleBlur}
                        />
                        {displayVahanDrawerButton && (
                          <button
                            type="button"
                            className="vehicle-number-open-btn"
                            onClick={() =>
                              setIsVahanVerificationDrawerOpen(true)
                            }
                            aria-label="Open vehicle in new tab"
                          >
                            <DetailLinkIcon className="icon-size-md" />
                          </button>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xxl={3} xl={3} lg={4} md={4}>
                    <div className="div-flex-column">
                      <span className="text-sm text-bold">Plant</span>
                      <div className="input-wrapper">
                        <CustomMultiSelect
                          name="plant"
                          isMulti={false}
                          className="multi-select-input-section margin-top-5"
                          data-testid="plant-select"
                          data-required-field
                          onChange={(selectedOption) => {
                            handleOnChange({
                              target: {
                                name: 'plant',
                                value: selectedOption?.value || null,
                              },
                            });
                          }}
                          options={plantDataOption}
                          classNamePrefix="react-select"
                          placeholder="Search Plant"
                          value={plantDataOption.find(
                            (option) => option.value === formData.plant,
                          )}
                        />
                        <span className="form-input-section-icon">
                          <Search className="icon-size-sm" color="#9F9F9F" />
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col xxl={3} xl={3} lg={4} md={4}>
                    <div className="div-flex-column">
                      <div className="div-flex-row cg-5">
                        <span className="text-sm text-bold">
                          Workflow
                          <span className="text-red">&nbsp;*</span>
                        </span>
                      </div>
                      <div className="input-wrapper">
                        <CustomMultiSelect
                          name="workflowId"
                          isMulti={false}
                          data-testid="workflow-select"
                          className={`margin-top-5 ${formErrors.workflowId ? 'react-select-input-error' : 'multi-select-input-section'}`}
                          onChange={(selectedOption) => {
                            handleOnChange({
                              target: {
                                name: 'workflowId',
                                value: selectedOption?.value || null,
                              },
                            });
                          }}
                          options={workflowDataOption}
                          classNamePrefix="react-select"
                          placeholder="Select Workflow"
                          value={workflowDataOption.find(
                            (option) => option.value === formData.workflowId,
                          )}
                        />
                        {/* for handling form error */}
                        <input
                          type="hidden"
                          name="workflowId"
                          data-required-field
                          value={formData.workflowId || ''}
                        />
                        <span className="form-input-section-icon">
                          <Search className="icon-size-sm" color="#9F9F9F" />
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col xxl={3} xl={3} lg={4} md={4}>
                    <div className="div-flex-column">
                      <div className="div-flex-row cg-5">
                        <span className="text-sm text-bold">Direction</span>
                      </div>
                      <div className="div-flex-row cg-20 margin-top-5">
                        <div className="div-flex-row cg-5">
                          <input
                            type="radio"
                            name="vehicleTrackingType"
                            value="1"
                            checked={formData.vehicleTrackingType === '1'}
                            onChange={handleOnChange}
                            disabled={
                              !noPermissionsAssigned && !hasInwardPermission
                            }
                          />
                          <span className="text-sm">Inward</span>
                        </div>
                        <div className="div-flex-row cg-5">
                          <input
                            type="radio"
                            name="vehicleTrackingType"
                            value="2"
                            checked={formData.vehicleTrackingType === '2'}
                            onChange={handleMoveToCreateOutward}
                            disabled={
                              !noPermissionsAssigned && !hasOutwardPermission
                            }
                          />
                          <span className="text-sm">Outward</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            {/* select purchase order card */}
            <div className="container-card-shadow border-radius-4 container-padding-5 margin-top-10 min-height-20vh">
              <div className="container-padding-10">
                <span className="text-xxl text-bold">
                  Select Purchase Order
                </span>
                <Row>
                  <Col
                    xxl={4}
                    xl={4}
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    className="form-input-container"
                  >
                    <div className="div-flex-column">
                      <span className="text-sm text-bold">PO Number</span>
                      <div className="div-flex-row">
                        <div className="input-wrapper">
                          <input
                            className="form-input-section margin-top-5"
                            type="text"
                            data-testid="po-search-input"
                            placeholder="Document Number"
                            name="search"
                            onChange={(e) =>
                              setPurchaseOrderSearchedTerm(e.target.value)
                            }
                          />
                          <span className="form-input-section-icon">
                            <Search className="icon-size-sm" color="#9F9F9F" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xxl={8} xl={8} lg={8} md={8} sm={12} xs={12}>
                    <Row>
                      <Col
                        xxl={9}
                        xl={9}
                        lg={9}
                        md={9}
                        sm={12}
                        xs={12}
                        className="form-input-container"
                      >
                        <div className="div-flex-column">
                          <span className="text-sm text-bold">
                            Business Partner
                          </span>
                          <div className="input-wrapper">
                            <CustomMultiSelect
                              name="business-partner"
                              isMulti={false}
                              data-testid="business-partner-select"
                              className="multi-select-input-section margin-top-5"
                              onChange={(selectedOption) => {
                                setBusinessPartnerSearchTerm('');
                                handleBusinessPartnerSelectChange({
                                  target: {
                                    name: 'business-partner',
                                    value: selectedOption?.value || null,
                                  },
                                });
                              }}
                              onInputChange={(inputValue, { action }) => {
                                if (action === 'input-change') {
                                  setBusinessPartnerSearchTerm(inputValue);
                                }
                              }}
                              inputValue={businessPartnerSearchTerm}
                              options={businessPartnersWithOpenPoOptions}
                              classNamePrefix="react-select"
                              placeholder="Search Business Partner"
                              value={businessPartnersWithOpenPoOptions.find(
                                (option) =>
                                  option.value === selectedBusinessPartner,
                              )}
                              filterOption={() => true}
                            />
                            <span className="form-input-section-icon">
                              <Search
                                className="icon-size-sm"
                                color="#9F9F9F"
                              />
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col
                        xxl={3}
                        xl={3}
                        lg={3}
                        md={3}
                        sm={12}
                        xs={12}
                        className="form-input-container d-flex flex-column justify-content-end"
                      >
                        <div className="div-flex-column">
                          <button
                            type="button"
                            data-testid="po-search-btn"
                            disabled={isSearching}
                            className={`search-purchase-order-btn theme-background-${themeColor}`}
                            onClick={() => handlePurchaseOrderSearchSubmit()}
                          >
                            {isSearching ? 'Searching…' : 'Search'}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {/* list of purchase order */}
                {filteredPurchaseOrderList.length === 0 ? (
                  <div className="document-empty-state-container">
                    <img
                      src={SelectPurchaseOrderEmptyState}
                      alt="Search PO to proceed"
                      style={{
                        maxWidth: '100%',
                        width: '200px',
                        height: 'auto',
                      }}
                    />
                    <div className="margin-top-10">
                      <span className="text-xxl text-bold">
                        Start by selecting PO documents
                      </span>
                    </div>
                    <div>
                      <span className="text-md text-grey">
                        Enter a PO number or select a business partner to begin.{' '}
                        <br /> Then click Search to see available purchase
                        orders for tracking.
                      </span>
                    </div>
                  </div>
                ) : null}
                {filteredPurchaseOrderList.length > 0 ? (
                  <hr className="divider-line" />
                ) : null}
                {filteredPurchaseOrderList.length > 0 && (
                  <div className="div-flex-row div-flex-end ">
                    <div className="div-flex-row cg-10 right-padding">
                      <span className="text-sm text-grey">Collapse All</span>
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCollapseTableAccordion(e.target.checked)
                        }
                      />
                    </div>
                  </div>
                )}
                <div className="margin-top-5">
                  {filteredPurchaseOrderList.length > 0 && (
                    <div className="purchase-order-line-item-card border-radius-4">
                      {filteredPurchaseOrderList?.map(
                        (purchaseOrderObj, index) => (
                          <div key={purchaseOrderObj.id}>
                            <div className="div-space-between div-align-center border-bottom left-padding right-padding">
                              <div className="div-flex-row cg-10">
                                <input
                                  type="checkbox"
                                  data-testid="purchase-order-checkbox"
                                  checked={selectedPurchaseOrder.some(
                                    (poObj) => poObj.id === purchaseOrderObj.id,
                                  )}
                                  onChange={(e) =>
                                    handlePurchaseOrderSelection(
                                      e,
                                      purchaseOrderObj,
                                    )
                                  }
                                  disabled={purchaseOrderObj.status === '4'}
                                />
                                <div className="div-flex-row div-align-center cg-10">
                                  <span className="text-sm">
                                    {purchaseOrderObj.po_number} |&nbsp;
                                    {purchaseOrderObj.purchase_order_date}
                                  </span>
                                  <span
                                    className={
                                      purchaseOrderObj.status === '4'
                                        ? 'red-pill bg-red text-sm margin-left-10'
                                        : 'green-pill bg-green text-sm margin-left-10'
                                    }
                                  >
                                    {purchaseOrderObj.status}
                                  </span>
                                </div>
                              </div>
                              <button
                                className="btn btn-sm"
                                onClick={() => toggleAccordion(index)}
                                aria-label={
                                  openPoTableIndexes.includes(index)
                                    ? 'Collapse row'
                                    : 'Expand row'
                                }
                              >
                                <ArrowDown
                                  className={`icon-size-md ${openPoTableIndexes.includes(index) ? 'rotate-icon-180' : ''}`}
                                  color="#9F9F9F"
                                />
                              </button>
                            </div>
                            <div className="po-table-wrapper">
                              {openPoTableIndexes.includes(index) && (
                                <table>
                                  <thead
                                    style={{
                                      borderBottom: '1px solid #ccc',
                                      borderWidth: '1px',
                                    }}
                                  >
                                    <tr
                                      style={{
                                        borderBottom: '1px solid #ccc',
                                        borderWidth: '1px',
                                      }}
                                    >
                                      <th>Plant</th>
                                      <th>Material Code</th>
                                      <th>Material Description</th>
                                      <th>PO Quantity</th>
                                      <th>Open Quantity</th>
                                      <th>Blocked Quantity</th>
                                      <th>Rate</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {purchaseOrderObj.items
                                      .filter(
                                        (item) =>
                                          Number(item.open_quantity) > 0,
                                      )
                                      .map((item) => (
                                        <tr key={item.id}>
                                          <td>{item.plant}</td>
                                          <td>{item.product_name}</td>
                                          <td>{item.product_description}</td>
                                          <td>
                                            {item.po_quantity}&nbsp;
                                            {item.unit}
                                          </td>
                                          <td>
                                            {item.open_quantity}&nbsp;
                                            {item.unit}
                                          </td>
                                          <td>
                                            {item.blocked_quantity
                                              ? `${item.blocked_quantity} ${item.unit}`
                                              : '0'}
                                          </td>
                                          <td>{item.rate}</td>
                                          <td>{item.total}</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
                <div className="footer-container border-radius-4 py-2">
                  <div className="div-space-between div-align-center footer-content-for-page">
                    <div className="div-flex-row cg-20 pagination-container div-align-center">
                      <div className="div-flex-row cg-10 div-align-center">
                        <button
                          className="icon-btn-none"
                          onClick={handlePreviousPage}
                          aria-label="Enter page number"
                          type="button"
                        >
                          <ArrowLeft className="icon-size-sm" />
                        </button>
                        <input
                          className="page-number-container text-sm"
                          value={formattedPageNumber}
                          onChange={handleInputChange}
                          max={totalPages}
                        />
                        <button
                          className="icon-btn-none"
                          onClick={handleNextPage}
                          aria-label="Enter page number"
                          type="button"
                        >
                          <ArrowRight className="icon-size-sm" />
                        </button>
                      </div>
                      <span className="text-grey text-sm">
                        Page {pageNumber} of {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col
          className={`vehicle-tracking-theme-scrollable-container-${themeColor} scrollable-container`}
          xxl={4}
          xl={4}
          lg={4}
        >
          <div className="col-container">
            <div className="container-card-shadow border-radius-4 container-padding-5">
              <div className="container-padding-10">
                <span className="text-xxl text-bold">Selected Documents</span>
                {/* for handling form error */}
                {formErrors.po && selectedPurchaseOrder?.length === 0 ? (
                  <div>
                    <p className="text-red text-sm">
                      Please select a purchase order to proceed
                    </p>
                  </div>
                ) : null}
                <input
                  type="hidden"
                  name="po"
                  data-required-field
                  value={selectedPurchaseOrder || ''}
                />
                {selectedPurchaseOrder?.length > 0 &&
                  selectedPurchaseOrder?.map((poObj) => (
                    <Row key={poObj.id}>
                      <Col
                        xxl={12}
                        xl={12}
                        lg={12}
                        md={12}
                        className="form-input-container margin-top-10"
                      >
                        <div className="div-flex-column">
                          <div className="div-flex-row cg-5">
                            <span className="text-md text-bold">
                              PO Number: {poObj.po_number}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ))}
                {selectedPurchaseOrder?.length === 0 ? (
                  <div className="div-flex-column cg-20 margin-top-10">
                    <div className="document-empty-state-container">
                      <img
                        src={SelectedPurchaseOrderEmptyState}
                        alt="No PO selected"
                        style={{
                          maxWidth: '100%',
                          width: '120px',
                          height: 'auto',
                        }}
                      />
                      <div className="margin-top-10">
                        <span className="text-xxl text-bold">
                          No PO Documents Selected Yet
                        </span>
                      </div>
                      <div>
                        <span className="text-md text-grey">
                          Once you find the correct purchase orders, <br />{' '}
                          Select them to add to this vehicle entry. <br />{' '}
                          You&apos;ll see the selected documents listed here.
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Drawer
        anchor="right"
        open={isVahanVerificationDrawerOpen}
        onClose={() => {
          setIsVahanVerificationDrawerOpen(false);
        }}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1 }}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: '100%',
              md: '50%',
            },
          },
        }}
      >
        <VehicleDetail
          vehicleNumber={formData.vehicleNumber}
          onClose={() => setIsVahanVerificationDrawerOpen(false)}
        />
      </Drawer>
    </div>
  );
}

export default BasicDetails;
